/**
 * Route List — Day tabs + employee route cards.
 * DayTabs renders above the map (full width).
 * RouteCards renders in the sidebar.
 * RouteList combines both (for standalone use).
 */

import React, { useState, useMemo } from 'react';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREV = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

/** Shared hook: groups per_route by day, computes stats. */
export function useRouteData(score) {
  return useMemo(() => {
    if (!score?.per_route || !Array.isArray(score.per_route)) {
      return { days: [], routesByDay: {}, dayStats: {} };
    }
    const byDay = {};
    const stats = {};
    for (const route of score.per_route) {
      const day = route.day;
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(route);
    }
    const dayList = Object.keys(byDay).sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
    for (const day of dayList) {
      const routes = byDay[day];
      const totalUtil = routes.reduce((s, r) => s + (r.utilization || 0), 0);
      stats[day] = {
        miles: routes.reduce((s, r) => s + (r.distance_miles || 0), 0),
        stops: routes.reduce((s, r) => s + (r.num_stops || 0), 0),
        count: routes.length,
        avgUtil: routes.length > 0 ? (totalUtil / routes.length) * 100 : 0,
      };
    }
    return { days: dayList, routesByDay: byDay, dayStats: stats };
  }, [score]);
}

/** Day tabs — horizontal strip, rendered above the map. */
export function DayTabs({ days, dayStats, currentDay, onSelectDay }) {
  return (
    <div className="ro-day-tabs">
      {days.map(day => {
        const s = dayStats[day];
        const overloaded = s.avgUtil > 100;
        return (
          <button
            key={day}
            className={`ro-day-tab ${day === currentDay ? 'ro-day-tab-active' : ''}`}
            onClick={() => onSelectDay(day)}
            title={day}
            aria-pressed={day === currentDay}
            aria-label={`${day} — ${s.count} routes, ${s.stops} stops, ${s.miles.toFixed(0)} miles${overloaded ? ' — OVERLOADED' : ''}`}
            style={overloaded ? { outline: '2px solid #d32f2f', outlineOffset: '-1px', zIndex: 1, position: 'relative' } : undefined}
          >
            <div className="ro-day-tab-name">{DAY_ABBREV[day] || day}</div>
            <div className="ro-day-tab-detail">{s.count} routes &middot; {s.stops} stops</div>
            <div className="ro-day-tab-detail" style={overloaded ? { color: '#d32f2f', fontWeight: 700 } : undefined}>
              {s.miles.toFixed(0)} mi &middot; {overloaded ? '\u26D4 ' : ''}{s.avgUtil.toFixed(0)}% util
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** Route cards — simple list in sidebar. */
export function RouteCards({ routes, currentDay, techColors, selectedRoute, onSelectRoute }) {
  const handleClick = (route) => {
    const isSame = selectedRoute?.day === currentDay && selectedRoute?.technician === route.technician;
    onSelectRoute(isSame ? null : { day: currentDay, technician: route.technician });
  };

  return (
    <div className="ro-route-cards">
      {routes.map((route, idx) => {
        const color = techColors?.[route.technician] || '#999';
        const isSelected = selectedRoute?.day === currentDay &&
          selectedRoute?.technician === route.technician;
        return (
          <div
            key={idx}
            className={`ro-route-item ${isSelected ? 'ro-route-item-active' : ''}`}
            onClick={() => handleClick(route)}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(route); } }}
            aria-label={`${route.technician} — ${route.num_stops} stops, ${route.distance_miles?.toFixed(1)} miles`}
            aria-selected={isSelected}
          >
            <div className="ro-route-color" style={{ background: color }} />
            <div className="ro-route-info">
              <div className="ro-route-tech">{route.technician}</div>
              <div className="ro-route-detail">
                {route.num_stops} stops &middot; {route.distance_miles?.toFixed(1)} mi
                {route.utilization != null && ` \u00B7 ${(route.utilization * 100).toFixed(0)}%`}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Combined RouteList (backward-compatible, not used by ResultsPanel anymore). */
export default function RouteList({ score, techColors, selectedRoute, onSelectRoute }) {
  const [activeDay, setActiveDay] = useState(null);
  const { days, routesByDay, dayStats } = useRouteData(score);

  if (days.length === 0) return <p>No route data available.</p>;

  const currentDay = (activeDay && days.includes(activeDay)) ? activeDay : days[0];
  const currentRoutes = routesByDay[currentDay] || [];

  return (
    <div className="ro-route-list">
      <DayTabs
        days={days}
        dayStats={dayStats}
        currentDay={currentDay}
        onSelectDay={(day) => { setActiveDay(day); onSelectRoute(null); }}
      />
      <RouteCards
        routes={currentRoutes}
        currentDay={currentDay}
        techColors={techColors}
        selectedRoute={selectedRoute}
        onSelectRoute={onSelectRoute}
      />
    </div>
  );
}
